import { OptionType } from '@components/MultiSelectAutocomplete'
import { Autocomplete, Paper, Stack, TextField } from '@mui/material'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import { useEffect, useState } from 'react'
import { LicenseOption, getAllLicenseTypes } from '@enums/licenses'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { LoadingButton } from '@mui/lab'
import API from '@enums/api'

type LicenseSelect = OptionType & LicenseOption

export default function CreateUKLicensesPage() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>()
    const [selectedOrg, setSelectedOrg] = useState<OptionType>()
    const [orgs, setOrgs] = useState<OptionType[]>([])
    const [licenseTypes, setLicenseTypes] = useState<LicenseSelect[]>([])
    const [selectedType, setSelectedType] = useState<LicenseSelect>()
    const [seats, setSeats] = useState<number>(0)

    const submittable = Boolean(selectedOrg && selectedType && seats > 0)

    useEffect(() => {
        getOrgs()

        const licenseOptions = getAllLicenseTypes({
            exclude_starter: true,
            only_term: 'monthly',
        })
        setLicenseTypes(
            licenseOptions.map(
                (l) =>
                    ({
                        id: l.label,
                        ...l,
                    }) as unknown as LicenseSelect
            )
        )
    }, [])

    async function getOrgs() {
        setLoading(true)

        const res = await API.searches.getUKLicenseHolders()

        if (!res.ok) {
            setError(res.status?.toString() ?? '500')
            setLoading(false)
            return
        }

        const data = res.value

        setOrgs(data)

        setLoading(false)
    }

    function onSelectedOrgChanged(
        _event: React.ChangeEvent<object>,
        v: OptionType | null
    ) {
        setSelectedOrg(v ?? undefined)
    }

    function onLicenseTypeChanged(
        _event: React.ChangeEvent<object>,
        v: LicenseSelect | null
    ) {
        setSelectedType(v ?? undefined)
    }

    async function submitLicenseRequest() {
        if (!submittable) return

        setLoading(true)

        const res = await API.ha.licenses.createUKLicenses({
            licenses: [
                {
                    licenseTier: selectedType!.tier,
                    term: selectedType!.term,
                    quantity: seats,
                },
            ],
            org_id: selectedOrg!.id as string,
        })

        if (!res.ok) {
            console.error(res)
            setError(res.message)
            setLoading(false)
            return
        }

        enqueueSnackbar({
            variant: 'success',
            message: 'Licenses created!',
        })

        setSelectedOrg(undefined)
        setSelectedType(undefined)
        setSeats(0)
        setLoading(false)
    }

    if (error) {
        return <DashboardErrorView error={error} />
    }

    return (
        <DashboardLayout loading={loading}>
            <Paper
                sx={{
                    mx: 'auto',
                    width: '30em',
                    height: '50%',
                }}
            >
                <Stack direction={'column'} rowGap={'1em'}>
                    <Autocomplete
                        onChange={onSelectedOrgChanged}
                        options={orgs}
                        value={selectedOrg}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                style={{ textTransform: 'capitalize' }}
                            >
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={'Organization'}
                                placeholder={'Organization...'}
                            />
                        )}
                    />

                    {/* License Select */}
                    <Autocomplete
                        options={licenseTypes}
                        value={selectedType}
                        aria-label="License Type"
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        onChange={onLicenseTypeChanged}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                style={{ textTransform: 'uppercase' }}
                            >
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={'License Type'}
                                placeholder={'License Type...'}
                            />
                        )}
                    />

                    {/* Number of seats */}
                    <TextField
                        label="Licenses"
                        value={seats}
                        onChange={(e) => setSeats(Number(e.target.value))}
                        type="tel"
                        inputProps={{
                            min: 0,
                        }}
                    />

                    <LoadingButton
                        loading={loading}
                        disabled={!submittable}
                        onClick={submitLicenseRequest}
                    >
                        Request Licenses
                    </LoadingButton>
                </Stack>
            </Paper>
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}
